import React from 'react';
import { OptImg } from '../components/ui/OptImg';
import { Link } from 'react-router-dom';
import { ArrowRight, SunDim, Ruler, ShieldCheck, Sparkles, Home, Building2, ChefHat, Bath } from 'lucide-react';
import { Layout } from '../components/layout/Layout';
import { Breadcrumbs } from '../components/website/Breadcrumbs';
import { ProductCmsContent } from '../components/website/ProductCmsContent';
import { ProductNextSteps } from '../components/website/ProductNextSteps';
import { ConsultationPrompt } from '../components/website/ConsultationPrompt';

const IMG = (h) => `https://static.prod-images.emergentagent.com/jobs/40af1b99-2067-4f0a-9458-756e0f39b6b0/images/${h}.jpeg`;
const IMAGES = {
  wood: IMG('4d534a0248fae332caf9cafbd47fd72b7b8865a0c9c94af5d924fc90e8732c8a'),
  faux: IMG('aac751b42747084d96ba25e0e7bb4725f5d93287690d85f8e3c52d0330b9bb75'),
  aluminum: IMG('3b36fdc321ac0ad0576aa2be2751603db1b246c889f12f130792e6f686d6ed27'),
  vertical: IMG('8355c5bc73d407ab4793d0838b751301f8c7840c7d685b3270fa4cc61cafff3d'),
  residential: IMG('87734fb74591a04068157aaaf72fc7c5917c2eab42e50848a5504d4bd781b705'),
  commercial: IMG('24f52fb6eb7397a267128903d2ad68be5f6a1c229f90b6974b3af34120da33ea'),
};

const HERO = IMAGES.wood;
const GALLERY = [
  { src: IMAGES.residential, alt: 'Matching wood blinds in a brownstone dining room', caption: 'Residential' },
  { src: IMAGES.commercial, alt: 'Wood blinds across a Manhattan boardroom', caption: 'Commercial' },
];
const BENEFITS = [
  { icon: SunDim, title: 'Precise light control', text: 'Tilt slats to soften glare, direct daylight, or close fully for privacy, all in a single motion.' },
  { icon: Ruler, title: 'Made to measure', text: 'Each blind is cut to your exact window dimensions for a clean inside or outside mount.' },
  { icon: ShieldCheck, title: 'Built to last', text: 'Quality headrails, ladders and slats engineered for years of smooth daily use.' },
  { icon: Sparkles, title: 'Effortless upkeep', text: 'Durable finishes that wipe clean and hold their color and shape over time.' },
];
const APPLICATIONS = [
  { icon: Home, title: 'Living & Bedrooms', text: 'Warm, tailored light control for everyday spaces.' },
  { icon: ChefHat, title: 'Kitchens', text: 'Moisture-resistant options that are easy to clean.' },
  { icon: Bath, title: 'Bathrooms', text: 'Faux wood and aluminum stand up to humidity.' },
  { icon: Building2, title: 'Offices & Studios', text: 'Glare reduction for screens and workspaces.' },
];
const STYLES = [
  { name: 'Wood', img: IMAGES.wood, text: 'Real hardwood slats with rich, natural grain for a warm, classic look.' },
  { name: 'Faux Wood', img: IMAGES.faux, text: 'The look of wood with moisture resistance, ideal for kitchens and baths.' },
  { name: 'Aluminum', img: IMAGES.aluminum, text: 'Slim, modern slats with a crisp profile and lasting durability.' },
  { name: 'Vertical', img: IMAGES.vertical, text: 'Vertical vanes for sliding doors and wide expanses of glass.' },
];

const Hero = () => (
  <section className="relative h-[78vh] min-h-[520px] overflow-hidden" data-testid="blinds-hero">
    <OptImg priority src={HERO} alt="Elegant living room with wood blinds" className="absolute inset-0 h-full w-full object-cover" />
    <div className="absolute inset-0 bg-gradient-to-r from-black/75 via-black/40 to-transparent" />
    <div className="relative container-xl h-full flex flex-col justify-end pb-16 md:pb-24 text-white">
      <Breadcrumbs light className="mb-6" />
      <span className="label-overline text-amber-400">Window Treatments</span>
      <h1 className="mt-3 font-serif font-light !text-5xl md:!text-7xl tracking-tight" data-testid="blinds-hero-title">Custom Blinds</h1>
      <p className="mt-5 max-w-xl text-base md:text-lg text-white/80 leading-relaxed">Clean lines, precise light control and enduring craftsmanship, custom-made for every window in your home.</p>
      <Link to="/quote" className="mt-8 inline-flex w-fit items-center gap-2 bg-amber-500 hover:bg-amber-600 text-white px-7 py-3.5 text-sm font-medium uppercase tracking-[0.2em] transition-colors" data-testid="blinds-hero-quote-btn">
        Request a Quote <ArrowRight className="w-4 h-4" />
      </Link>
    </div>
  </section>
);

const Intro = () => (
  <section className="container-xl py-20 md:py-28 grid md:grid-cols-12 gap-10 md:gap-16 items-start" data-testid="blinds-intro">
    <div className="md:col-span-5">
      <span className="label-overline text-amber-600">The Collection</span>
      <h2 className="mt-3 font-serif !text-3xl md:!text-5xl font-light leading-tight text-neutral-900">A timeless way to shape the light</h2>
    </div>
    <div className="md:col-span-7 space-y-5 text-neutral-600 leading-relaxed md:pt-10">
      <p>Blinds remain one of the most versatile window treatments ever designed. With a simple tilt, they move from soft filtered daylight to complete privacy, adapting to the room, the hour and the season.</p>
      <p>Our blinds are custom-made to your exact measurements, in natural wood, moisture-resistant faux wood, sleek aluminum and vertical systems, and professionally installed by the Apollo team.</p>
    </div>
  </section>
);

const Gallery = () => (
  <section className="container-xl pb-20 md:pb-28 grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5" data-testid="blinds-gallery">
    {GALLERY.map((g, i) => (
      <figure key={g.src} className="relative overflow-hidden bg-neutral-200 aspect-[3/2]">
        <OptImg sizes="(min-width: 768px) 50vw, 100vw" src={g.src} alt={g.alt} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 hover:scale-[1.03]" data-testid={`blinds-gallery-img-${i}`} />
        <figcaption className="absolute left-5 bottom-5 bg-white/90 px-3 py-1.5 text-[11px] uppercase tracking-[0.25em] text-neutral-800">{g.caption}</figcaption>
      </figure>
    ))}
  </section>
);

const IconGrid = ({ id, eyebrow, title, items, dark }) => (
  <section className={dark ? 'bg-neutral-950 text-white' : 'bg-[#f6f2eb]'} data-testid={`blinds-${id}`}>
    <div className="container-xl py-20 md:py-28">
      <span className={`label-overline ${dark ? 'text-amber-400' : 'text-amber-600'}`}>{eyebrow}</span>
      <h2 className="mt-3 font-serif !text-3xl md:!text-5xl font-light max-w-2xl">{title}</h2>
      <div className="mt-12 md:mt-16 grid sm:grid-cols-2 lg:grid-cols-4 gap-10">
        {items.map(({ icon: Icon, title: t, text }) => (
          <div key={t} className={`border-t pt-6 ${dark ? 'border-white/15' : 'border-neutral-300'}`}>
            <Icon className={`w-6 h-6 ${dark ? 'text-amber-400' : 'text-amber-700'}`} strokeWidth={1.5} />
            <h3 className="mt-5 font-serif !text-xl md:!text-2xl font-light">{t}</h3>
            <p className={`mt-2 text-sm leading-relaxed ${dark ? 'text-white/65' : 'text-neutral-600'}`}>{text}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const Styles = () => (
  <section className="container-xl py-20 md:py-28" data-testid="blinds-styles">
    <span className="label-overline text-amber-600">Style Examples</span>
    <h2 className="mt-3 font-serif !text-3xl md:!text-5xl font-light text-neutral-900">Four ways to dress a window</h2>
    <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
      {STYLES.map((s) => (
        <article key={s.name} className="group" data-testid={`blinds-style-${s.name.toLowerCase().replace(/\s+/g, '-')}`}>
          <div className="aspect-[4/5] overflow-hidden bg-neutral-200">
            <OptImg sizes="(min-width: 1024px) 25vw, (min-width: 640px) 50vw, 100vw" src={s.img} alt={`${s.name} blinds`} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
          </div>
          <h3 className="mt-5 font-serif !text-2xl font-light text-neutral-900">{s.name}</h3>
          <p className="mt-2 text-sm text-neutral-600 leading-relaxed">{s.text}</p>
        </article>
      ))}
    </div>
  </section>
);

const Cta = () => (
  <section className="relative overflow-hidden bg-neutral-900 text-white" data-testid="blinds-cta">
    <OptImg loading="lazy" src={IMAGES.residential} alt="" className="absolute inset-0 h-full w-full object-cover opacity-30" />
    <div className="relative container-xl py-20 md:py-28 flex flex-col md:flex-row md:items-end md:justify-between gap-8">
      <div>
        <h2 className="font-serif !text-3xl md:!text-5xl font-light max-w-xl">Ready to bring blinds into your space?</h2>
        <p className="mt-4 text-white/70 max-w-lg">Tell us about your windows and we will prepare a tailored quote, with measuring and installation by our team.</p>
      </div>
      <Link to="/quote" className="inline-flex w-fit items-center gap-2 bg-amber-500 hover:bg-amber-600 text-white px-7 py-3.5 text-sm font-medium uppercase tracking-[0.2em] transition-colors" data-testid="blinds-cta-quote-btn">
        Request a Quote <ArrowRight className="w-4 h-4" />
      </Link>
    </div>
  </section>
);

export const BlindsPage = () => (
  <Layout>
    <div className="bg-neutral-50" data-testid="blinds-page">
      <Hero />
      <Intro />
      <Gallery />
      <IconGrid id="benefits" eyebrow="Why Blinds" title="Key benefits" items={BENEFITS} dark />
      <Styles />
      <IconGrid id="applications" eyebrow="Applications" title="Suited to every room" items={APPLICATIONS} />
      <ProductCmsContent slug="blinds"><ConsultationPrompt /></ProductCmsContent>
      <ProductNextSteps slug="blinds" name="blinds" />
      <Cta />
    </div>
  </Layout>
);
